/**
 * Desk session destinations — search index + header/rail quick actions.
 *
 * Primary navigation is the sidebar. Shortcuts on that rail are Trade, Chat, Help.
 * Chat is also a header utility on the far right. The header names the current surface.
 *
 * Locked primary rail: Dashboard, Markets, Portfolio, Agents, Trade,
 * Activity, Settings. Chat and Libraries stay discoverable in search + quick actions.
 */

export type DeskSessionId =
  | 'dashboard'
  | 'markets'
  | 'portfolio'
  | 'agents'
  | 'trading'
  | 'activity'
  | 'settings'
  | 'chat'
  | 'libraries'

export interface DeskSessionLink {
  id: DeskSessionId
  /** Clear chrome label */
  label: string
  /** Short description for search / aria */
  description: string
  href: string
  /** Match active state: pathname equals or starts with this prefix */
  matchPrefixes: readonly string[]
  /** Optional search tag */
  tag: string
}

/**
 * Locked seven primary destinations. Keep in sync with
 * `getClientSidebarItems()` and any local DeskSessionNav strip.
 */
export const PRIMARY_DESK_LINKS: readonly DeskSessionLink[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    description: 'Session, capital, and agent state on the client desk',
    href: '/client/dashboard',
    matchPrefixes: ['/client/dashboard', '/dashboard'],
    tag: 'dashboard',
  },
  {
    id: 'markets',
    label: 'Markets',
    description: 'Live market data and charts on the desk',
    href: '/market-data',
    matchPrefixes: ['/market-data'],
    tag: 'markets',
  },
  {
    id: 'portfolio',
    label: 'Portfolio',
    description: 'Positions, allocation, and performance',
    href: '/portfolio',
    matchPrefixes: ['/portfolio'],
    tag: 'portfolio',
  },
  {
    id: 'agents',
    label: 'Agents',
    description: 'Supervise autonomous trading agents on this desk',
    href: '/ai-trading',
    matchPrefixes: ['/ai-trading', '/algo-trading'],
    tag: 'agents',
  },
  {
    id: 'trading',
    label: 'Trade',
    description: 'Order execution and trading desk workflows',
    href: '/trading',
    matchPrefixes: ['/trading'],
    tag: 'execution',
  },
  {
    id: 'activity',
    label: 'Activity',
    description: 'Connection-scoped blotter for orders, fills, and agent events',
    href: '/activity',
    matchPrefixes: ['/activity'],
    tag: 'activity',
  },
  {
    id: 'settings',
    label: 'Settings',
    description: 'Plan, security, and desk preferences',
    href: '/settings',
    matchPrefixes: ['/settings'],
    tag: 'settings',
  },
]

/**
 * Secondary destinations — search and quick actions only. Not peer rail items.
 */
export const SECONDARY_DESK_LINKS: readonly DeskSessionLink[] = [
  {
    id: 'chat',
    label: 'Chat',
    description: 'Desk AI assistant for markets, risk, and execution context',
    href: '/chat-ai',
    matchPrefixes: ['/chat-ai'],
    tag: 'chat',
  },
  {
    id: 'libraries',
    label: 'Libraries',
    description: 'Multi-exchange grid and DCA strategy libraries (Desk+)',
    href: '/libraries',
    matchPrefixes: ['/libraries'],
    tag: 'libraries',
  },
]

/** Search destination map: primary rail first, then secondary destinations. */
export const DESK_SESSION_LINKS: readonly DeskSessionLink[] = [
  ...PRIMARY_DESK_LINKS,
  ...SECONDARY_DESK_LINKS,
]

/** True when pathname is this desk surface (or a nested path under it). */
export function isDeskSessionActive(
  pathname: string | null | undefined,
  link: DeskSessionLink
): boolean {
  if (!pathname) return false
  return link.matchPrefixes.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
}

/** Active desk session id for the current path, if any. */
export function getActiveDeskSessionId(
  pathname: string | null | undefined
): DeskSessionId | null {
  for (const link of DESK_SESSION_LINKS) {
    if (isDeskSessionActive(pathname, link)) return link.id
  }
  return null
}
